import { SearchOutlined } from "@ant-design/icons";
import {
  AutoComplete,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Typography,
  type SelectProps,
} from "antd";
import { debounce } from "lodash";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getPlacesByName,
  updateLeadSubmissionProgress,
} from "services/SauceService";
import {
  trackSelfCampaignCreateContinueClicked,
  trackSelfCampaignCreateProgressSaveFailed,
  trackSelfCampaignCreateProgressSaved,
  trackSelfCampaignCreateStepViewed,
  type SelfCampaignAnalyticsContext,
  type SelfCampaignCreateEventProps,
  type SelfCampaignUserFlow,
} from "analytics";
import { type LeadSubmission } from "interfaces/lead-submission";
import { buildSelfCampaignSearchWithId } from "./queryParams";
import { resolveSelfCampaignStepPath } from "./routePaths";
import { useSelfCampaignLead } from "./useSelfCampaignLead";
import {
  SelfServeCampaignBusinessType,
  SelfServeCampaignCreateStepState,
  SelfServeCampaignLocations,
} from "./types";
import SelfCampaignPageLayout from "./components/SelfCampaignPageLayout";
import SelfCampaignPrimaryCta from "./components/SelfCampaignPrimaryCta";
import SelfCampaignProgress from "./components/SelfCampaignProgress";
import "./SelfCampaignCreate.scss";

type SelfCampaignCreateFormValues = SelfServeCampaignCreateStepState & {
  storeAddressPlaceId?: string;
};

type AddressOption = {
  value: string;
  label: ReactNode;
  placeId: string;
  displayValue: string;
};

const BUSINESS_TYPE_OPTIONS: SelectProps["options"] = [
  {
    label: "Food & Beverage",
    options: [
      {
        value: "restaurant_food_beverage",
        label: "Restaurant / Food & Beverage",
      },
      { value: "cafe_coffee_shop", label: "Cafe / Coffee Shop" },
      { value: "bar_nightlife", label: "Bar / Nightlife" },
    ],
  },
  {
    label: "Travel & Hospitality",
    options: [
      { value: "hotel_resort", label: "Hotel / Resort" },
      { value: "travel_tourism", label: "Travel / Tourism" },
      { value: "event_venue", label: "Event Venue" },
    ],
  },
  {
    label: "Lifestyle",
    options: [
      { value: "beauty_skincare", label: "Beauty / Skincare" },
      { value: "fitness_wellness", label: "Fitness / Wellness" },
      { value: "fashion_apparel", label: "Fashion / Apparel" },
    ],
  },
  {
    label: "Commerce",
    options: [
      { value: "retail_local_shop", label: "Retail / Local Shop" },
      { value: "consumer_brand", label: "Consumer Brand" },
      { value: "e_commerce", label: "E-commerce" },
    ],
  },
  {
    label: "Experiences",
    options: [
      {
        value: "entertainment_attractions",
        label: "Entertainment / Attractions",
      },
      { value: "arts_culture", label: "Arts / Culture" },
    ],
  },
  {
    label: "Other",
    options: [{ value: "other", label: "Other" }],
  },
];

const DEFAULT_BUSINESS_TYPE: SelfServeCampaignBusinessType =
  "restaurant_food_beverage";
const DEFAULT_LOCATIONS: SelfServeCampaignLocations = 1;
const CREATE_REQUIRED_FIELDS_TOTAL = 5;
const BASE_PATH = "https://www.mustard.love";
const WELCOME_REDIRECT_PATH = `${BASE_PATH}/welcome`;

const normalizeLocationsValue = (
  value: unknown,
): SelfServeCampaignLocations => {
  const fromNumber =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;

  if (Number.isFinite(fromNumber)) {
    return Math.max(DEFAULT_LOCATIONS, Math.floor(fromNumber));
  }

  if (typeof value === "string") {
    const match = value.match(/\d+/);
    if (match) {
      const parsed = Number(match[0]);
      if (Number.isFinite(parsed)) {
        return Math.max(DEFAULT_LOCATIONS, Math.floor(parsed));
      }
    }
  }

  return DEFAULT_LOCATIONS;
};

const splitFullName = (
  value?: string,
): { firstName: string; lastName: string } => {
  const normalized = (value ?? "").trim();
  if (!normalized) {
    return { firstName: "", lastName: "" };
  }

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const getTrimmedString = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : "";
};

const hasNonEmptyString = (value: unknown): boolean => {
  return getTrimmedString(value).length > 0;
};

const toNullableLocationsCount = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return normalizeLocationsValue(value);
};

const resolveSelfCampaignPartner = (
  leadPartner: unknown,
  queryPartner: unknown,
): string | null => {
  const normalizedLeadPartner = getTrimmedString(leadPartner);
  if (normalizedLeadPartner) {
    return normalizedLeadPartner;
  }

  const normalizedQueryPartner = getTrimmedString(queryPartner);
  if (normalizedQueryPartner) {
    return normalizedQueryPartner;
  }

  return null;
};

const resolveSelfCampaignUserFlow = (
  partner: string | null,
): SelfCampaignUserFlow => {
  return partner ? "partner" : "self-campaign";
};

const normalizeBusinessType = (
  value: unknown,
): SelfServeCampaignBusinessType => {
  switch (value) {
    case "restaurant_food_beverage":
    case "cafe_coffee_shop":
    case "bar_nightlife":
    case "hotel_resort":
    case "travel_tourism":
    case "event_venue":
    case "beauty_skincare":
    case "fitness_wellness":
    case "fashion_apparel":
    case "retail_local_shop":
    case "consumer_brand":
    case "e_commerce":
    case "entertainment_attractions":
    case "arts_culture":
    case "other":
      return value;
    default:
      return DEFAULT_BUSINESS_TYPE;
  }
};

const mapLeadSubmissionToCreateValues = (
  leadSubmission: LeadSubmission,
): Partial<SelfCampaignCreateFormValues> => {
  const firstName = getTrimmedString(leadSubmission.firstName);
  const lastName = getTrimmedString(leadSubmission.lastName);
  const address = getTrimmedString(leadSubmission.address);
  const addressNumber = getTrimmedString(leadSubmission.addressNumber);

  const nextValues: Partial<SelfCampaignCreateFormValues> = {};

  if (firstName) {
    nextValues.firstName = firstName;
  }

  if (lastName) {
    nextValues.lastName = lastName;
  }

  if (address) {
    nextValues.storeAddress = address;
  }

  if (addressNumber) {
    nextValues.floorSuite = addressNumber;
  }

  if ("businessType" in leadSubmission) {
    nextValues.businessType = normalizeBusinessType(
      leadSubmission.businessType,
    );
  }

  if ("amountLocations" in leadSubmission) {
    nextValues.locations = normalizeLocationsValue(
      leadSubmission.amountLocations,
    );
  }

  return nextValues;
};

const buildHydratedCreateStepState = (
  values: Partial<SelfCampaignCreateFormValues>,
): SelfServeCampaignCreateStepState => {
  return {
    firstName: values.firstName || "",
    lastName: values.lastName || "",
    storeAddress: values.storeAddress || "",
    floorSuite: values.floorSuite || "",
    businessType: values.businessType || DEFAULT_BUSINESS_TYPE,
    locations: normalizeLocationsValue(values.locations),
  };
};

const countCompletedRequiredCreateFields = (
  values: Partial<SelfCampaignCreateFormValues>,
): number => {
  const completedFlags = [
    getTrimmedString(values.firstName).length > 0,
    getTrimmedString(values.lastName).length > 0,
    getTrimmedString(values.storeAddress).length > 0,
    getTrimmedString(values.businessType).length > 0,
    values.locations !== null &&
      values.locations !== undefined &&
      Number.isFinite(Number(values.locations)),
  ];

  return completedFlags.filter(Boolean).length;
};

const buildCreateTrackingProps = (
  values: Partial<SelfCampaignCreateFormValues>,
): SelfCampaignCreateEventProps => {
  return {
    business_type: getTrimmedString(values.businessType) || null,
    locations_count: toNullableLocationsCount(values.locations),
    has_floor_suite: hasNonEmptyString(values.floorSuite),
    has_store_address_place_id: hasNonEmptyString(values.storeAddressPlaceId),
    required_fields_completed: countCompletedRequiredCreateFields(values),
    required_fields_total: CREATE_REQUIRED_FIELDS_TOTAL,
  };
};

const SelfCampaignCreate: React.FC = () => {
  const [form] = Form.useForm<SelfCampaignCreateFormValues>();
  const location = useLocation();
  const navigate = useNavigate();
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [savingStepProgress, setSavingStepProgress] = useState(false);
  const [leadSubmissionError, setLeadSubmissionError] = useState<string | null>(
    null,
  );
  const hasTrackedStepViewedRef = useRef(false);
  const {
    leadSubmission,
    leadSubmissionId,
    loadingLeadSubmission,
    queryParams,
  } = useSelfCampaignLead({
    mode: "create",
  });

  const { businessName, name, partner: queryPartner } = queryParams;
  const resolvedPartner = useMemo(() => {
    return resolveSelfCampaignPartner(leadSubmission?.partner, queryPartner);
  }, [leadSubmission?.partner, queryPartner]);
  const userFlow = useMemo(() => {
    return resolveSelfCampaignUserFlow(resolvedPartner);
  }, [resolvedPartner]);
  const analyticsContext = useMemo<SelfCampaignAnalyticsContext>(() => {
    return {
      pathname: location.pathname,
      search: location.search,
      leadSubmissionId: leadSubmissionId ?? null,
      partner: resolvedPartner,
      userFlow,
    };
  }, [
    leadSubmissionId,
    location.pathname,
    location.search,
    resolvedPartner,
    userFlow,
  ]);

  const shouldHideCreateForm = loadingLeadSubmission;

  const initialValues = useMemo(() => {
    const leadBusinessName = getTrimmedString(leadSubmission?.businessName);
    const fallbackName = name || businessName || leadBusinessName;
    const parsedName = splitFullName(fallbackName);
    const hydratedValues = leadSubmission
      ? buildHydratedCreateStepState(
          mapLeadSubmissionToCreateValues(leadSubmission),
        )
      : undefined;

    return {
      firstName: hydratedValues?.firstName || parsedName.firstName,
      lastName: hydratedValues?.lastName || parsedName.lastName,
      storeAddress: hydratedValues?.storeAddress || "",
      floorSuite: hydratedValues?.floorSuite || "",
      businessType: hydratedValues?.businessType || DEFAULT_BUSINESS_TYPE,
      locations: normalizeLocationsValue(hydratedValues?.locations),
    } satisfies Partial<SelfCampaignCreateFormValues>;
  }, [businessName, leadSubmission, name]);

  const loadPlaces = useMemo(() => {
    return debounce(async (addressInput: string) => {
      if (!addressInput || addressInput.trim().length < 3) {
        setAddressOptions([]);
        return;
      }

      setLoadingAddress(true);
      try {
        const places: any = await getPlacesByName({
          name: addressInput.trim(),
        });

        const options: AddressOption[] = (places || []).map((place: any) => {
          const displayValue = `${place.name} - ${place.formatted_address}`;
          return {
            value: displayValue,
            placeId: place.place_id,
            displayValue,
            label: (
              <div className="self-campaign-create__address-option">
                <span className="self-campaign-create__address-name">
                  {place.name}
                </span>
                <span className="self-campaign-create__address-value">
                  {place.formatted_address}
                </span>
              </div>
            ),
          };
        });
        setAddressOptions(options);
      } catch (_error) {
        setAddressOptions([]);
      } finally {
        setLoadingAddress(false);
      }
    }, 350);
  }, []);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  useEffect(() => {
    return () => {
      loadPlaces.cancel();
    };
  }, [loadPlaces]);

  useEffect(() => {
    if (loadingLeadSubmission || hasTrackedStepViewedRef.current) {
      return;
    }

    hasTrackedStepViewedRef.current = true;
    trackSelfCampaignCreateStepViewed(
      analyticsContext,
      buildCreateTrackingProps(initialValues),
    );
  }, [analyticsContext, initialValues, loadingLeadSubmission]);

  const handleContinue = async () => {
    const values = form.getFieldsValue(true) as SelfCampaignCreateFormValues;
    const trackingProps = buildCreateTrackingProps(values);

    trackSelfCampaignCreateContinueClicked(analyticsContext, trackingProps);

    try {
      await form.validateFields();
    } catch (_error) {
      return;
    }

    const amountLocations = normalizeLocationsValue(values.locations);

    if (!leadSubmissionId) {
      window.location.assign(WELCOME_REDIRECT_PATH);
      return;
    }

    try {
      setSavingStepProgress(true);
      setLeadSubmissionError(null);

      await updateLeadSubmissionProgress(leadSubmissionId, {
        step: 1,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        address: values.storeAddress.trim(),
        addressNumber: values.floorSuite?.trim(),
        businessType: values.businessType,
        amountLocations,
      });
      trackSelfCampaignCreateProgressSaved(analyticsContext, trackingProps);
    } catch (_error) {
      trackSelfCampaignCreateProgressSaveFailed(analyticsContext, trackingProps);
      setLeadSubmissionError(
        "We could not save your progress. Please try again.",
      );
      setSavingStepProgress(false);
      return;
    }

    navigate({
      pathname: resolveSelfCampaignStepPath(location.pathname, "design"),
      search: buildSelfCampaignSearchWithId(location.search, leadSubmissionId),
    });

    setSavingStepProgress(false);
  };

  if (shouldHideCreateForm) {
    return (
      <div className="self-campaign-create__full-page-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <SelfCampaignPageLayout
      rootClassName="self-campaign-create"
      variant="create"
      leadPartner={leadSubmission?.partner}
      footer={
        shouldHideCreateForm ? null : (
          <SelfCampaignPrimaryCta
            label="Continue"
            mode="mobile-sticky"
            onClick={handleContinue}
            disabled={
              loadingAddress || loadingLeadSubmission || savingStepProgress
            }
          />
        )
      }
    >
      <SelfCampaignProgress
        title="Create your first campaign"
        activeStep={2}
        variant="create"
      />

      <section className="self-campaign-create__card">
        <div className="self-campaign-create__intro">
          <h2>
            Hi{" "}
            {businessName ||
              getTrimmedString(leadSubmission?.businessName) ||
              "there"}{" "}
            👋
          </h2>
          <Typography.Paragraph>
            Let&apos;s learn about your business. We&apos;ll tailor your
            campaign with hyper-local creators and recommendations to drive real
            visits.
          </Typography.Paragraph>
          {/* <div className="self-campaign-create__badge">
            <span>🎯</span>
            <span>Better data = Better matches</span>
          </div> */}
          {leadSubmissionError ? (
            <Typography.Text
              type="danger"
              className="self-campaign-create__lead-error"
            >
              {leadSubmissionError}
            </Typography.Text>
          ) : null}
        </div>

        {shouldHideCreateForm ? null : (
          <Form
            form={form}
            layout="vertical"
            className="self-campaign-create__form"
            autoComplete="off"
          >
            <section className="self-campaign-create__section">
              {/* <div className="self-campaign-create__section-title">
              <span className="self-campaign-create__section-icon">
                <UserOutlined />
              </span>
              <h3>Personal Information</h3>
            </div> */}
              <div className="self-campaign-create__grid self-campaign-create__grid--personal">
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: "First Name is required." },
                  ]}
                >
                  <Input placeholder="Jane" />
                </Form.Item>

                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: "Last Name is required." },
                  ]}
                >
                  <Input placeholder="Doe" />
                </Form.Item>
              </div>
            </section>

            <div className="self-campaign-create__divider" />

            <section className="self-campaign-create__section">
              {/* <div className="self-campaign-create__section-title">
              <span className="self-campaign-create__section-icon">
                <EnvironmentOutlined />
              </span>
              <h3>Business Location</h3>
            </div> */}
              <div className="self-campaign-create__grid self-campaign-create__grid--location">
                <Form.Item
                  label="Business Address"
                  name="storeAddress"
                  rules={[
                    {
                      required: true,
                      message: "Business Address is required.",
                    },
                  ]}
                >
                  <AutoComplete
                    options={addressOptions}
                    onSearch={(value) => {
                      form.setFieldValue("storeAddressPlaceId", undefined);
                      loadPlaces(value);
                    }}
                    onSelect={(_value, option) => {
                      const selectedOption = option as AddressOption;
                      form.setFieldValue(
                        "storeAddress",
                        selectedOption.displayValue,
                      );
                      form.setFieldValue(
                        "storeAddressPlaceId",
                        selectedOption.placeId,
                      );
                    }}
                  >
                    <Input
                      prefix={<SearchOutlined />}
                      placeholder="12415 Ventura Blvd..."
                    />
                  </AutoComplete>
                </Form.Item>

                <Form.Item
                  label={
                    <>
                      Floor / Suite{" "}
                      <span className="optional"> (Optional)</span>
                    </>
                  }
                  name="floorSuite"
                >
                  <Input placeholder="Suite 200" />
                </Form.Item>
              </div>
            </section>

            <div className="self-campaign-create__divider" />

            <section className="self-campaign-create__section">
              {/* <div className="self-campaign-create__section-title">
              <span className="self-campaign-create__section-icon">
                <ShopOutlined />
              </span>
              <h3>Business Details</h3>
            </div> */}
              <div className="self-campaign-create__grid self-campaign-create__grid--details">
                <Form.Item
                  label="Business Type"
                  name="businessType"
                  rules={[
                    { required: true, message: "Business Type is required." },
                  ]}
                >
                  <Select
                    options={BUSINESS_TYPE_OPTIONS}
                    classNames={{
                      popup: {
                        root: "self-campaign-business-type-dropdown",
                      },
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="Locations"
                  name="locations"
                  rules={[
                    { required: true, message: "Locations is required." },
                    {
                      type: "number",
                      min: 0,
                      message: "Locations must be at least 0.",
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>
            </section>

            <Form.Item name="storeAddressPlaceId" hidden>
              <Input />
            </Form.Item>

            <SelfCampaignPrimaryCta
              label="Continue"
              mode="inline"
              onClick={handleContinue}
              disabled={
                loadingAddress || loadingLeadSubmission || savingStepProgress
              }
            />
          </Form>
        )}
      </section>
    </SelfCampaignPageLayout>
  );
};

export default SelfCampaignCreate;
